import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Box, Typography, CircularProgress, Divider, TextField, Button, IconButton, Avatar } from '@mui/material';
import { ArrowBack, Edit, Logout, CheckCircle, ContentCut, Person, BookOnline } from '@mui/icons-material';
import NavBar from '../../components/NavBar';
import { colors, typography, radius, inputSx, btnPrimary } from '../../theme';

function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-MY', { day:'numeric', month:'short', year:'numeric' });
}

const StatChip = ({ icon, label, value, color = colors.primary }) => (
    <Box sx={{ flex:1, bgcolor: colors.bgCard, border:`1px solid ${colors.border}`, borderRadius:2.5, p:1.5, textAlign:'center' }}>
        <Box sx={{ display:'flex', justifyContent:'center', mb:0.5 }}>{icon}</Box>
        <Typography sx={{ fontFamily: typography.poppins, fontSize:24, fontWeight:700, color, lineHeight:1 }}>{value}</Typography>
        <Typography sx={{ fontSize:10, color: colors.textFaintest, mt:0.3 }}>{label}</Typography>
    </Box>
);

const BookingRow = ({ booking }) => {
    const statusColor = {
        waiting:   colors.primary,
        serving:   colors.success,
        completed: colors.info,
        cancelled: colors.error,
    }[booking.status] ?? colors.textFaint;

    return (
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5, py:1.2, borderBottom:`1px solid ${colors.border}11` }}>
            <Box sx={{ width:36, height:36, borderRadius:'50%', flexShrink:0, bgcolor: colors.bg, border:`1px solid ${colors.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {booking.type === 'walkin'
                    ? <Person sx={{ fontSize:16, color: colors.textMuted }} />
                    : <ContentCut sx={{ fontSize:14, color: colors.primary }} />}
            </Box>
            <Box sx={{ flex:1, minWidth:0 }}>
                <Typography sx={{ fontSize:13, fontWeight:600, color: colors.textPrimary }} noWrap>{booking.barberName ?? 'Barber'}</Typography>
                <Typography sx={{ fontSize:11, color: colors.textFaint }}>{formatDate(booking.scheduledAt)} · {booking.type === 'walkin' ? 'Walk-in' : 'Appointment'}</Typography>
            </Box>
            <Box sx={{ px:1, py:0.2, borderRadius:1, bgcolor:`${statusColor}15`, border:`1px solid ${statusColor}44` }}>
                <Typography sx={{ fontSize:10, color: statusColor, fontWeight:600 }}>{booking.status?.toUpperCase()}</Typography>
            </Box>
        </Box>
    );
};

const UserProfile = () => {
    const navigate = useNavigate();
    const [userData,  setUserData]  = useState(null);
    const [bookings,  setBookings]  = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [editing,   setEditing]   = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [saveMsg,   setSaveMsg]   = useState('');
    const [editName,  setEditName]  = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [uid,       setUid]       = useState(null);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate('/signin'); return; }
            setUid(user.uid);
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                if (snap.exists()) {
                    const data = { id: snap.id, ...snap.data(), email: user.email };
                    setUserData(data); setEditName(data.name ?? ''); setEditPhone(data.phone ?? '');
                }
            } catch (e) { console.error(e); }
            setLoading(false);
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!uid) return;
        const q = query(collection(db, 'bookings'), where('userId', '==', uid));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.scheduledAt?.toDate?.() ?? new Date(0)) - (a.scheduledAt?.toDate?.() ?? new Date(0)));
            setBookings(data);
        });
        return () => unsub();
    }, [uid]);

    const handleSave = async () => {
        if (!uid) return;
        setSaving(true);
        try {
            await updateDoc(doc(db, 'users', uid), { name: editName, phone: editPhone });
            setUserData(prev => ({ ...prev, name: editName, phone: editPhone }));
            setEditing(false); setSaveMsg('Profile updated!');
            setTimeout(() => setSaveMsg(''), 3000);
        } catch (e) { console.error(e); }
        setSaving(false);
    };

    const handleLogout = async () => { await signOut(auth); navigate('/'); };

    const totalBookings  = bookings.length;
    const completed      = bookings.filter(b => b.status === 'completed').length;
    const walkIns        = bookings.filter(b => b.type === 'walkin').length;
    const recentBookings = bookings.slice(0, 5);
    const initials       = userData?.name ? userData.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2) : '?';

    if (loading) return (
        <Box sx={{ minHeight:'100vh', bgcolor: colors.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    return (
        <Box sx={{ bgcolor: colors.bg, minHeight:'100vh', color: colors.textPrimary, pb:8 }}>
            <NavBar
                showSearch={false}
            />


            {/* Profile card */}
            <Box sx={{ px:3, mb:3 }}>
                <Box sx={{ bgcolor: colors.bgCard, border:`1px solid ${colors.border}`, borderRadius: radius.lg, overflow:'hidden', position:'relative' }}>
                    <Box sx={{ p:3 }}>

                        {/* Avatar + name */}
                        <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:3 }}>
                            <Avatar sx={{ width:64, height:64, flexShrink:0, bgcolor: colors.bg, border:`2px solid ${colors.primaryBorder}`, fontFamily: typography.poppins, fontSize:22, fontWeight:700, color: colors.primary }}>
                                {initials}
                            </Avatar>
                            <Box sx={{ flex:1, minWidth:0 }}>
                                <Typography sx={{ fontFamily: typography.poppins, fontSize:20, fontWeight:700, color: colors.textPrimary, lineHeight:1.2 }} noWrap>{userData?.name ?? 'Customer'}</Typography>
                                <Typography sx={{ fontSize:12, color: colors.textFaint, mt:0.3 }} noWrap>{userData?.email}</Typography>
                                {/* Keep customer blue as role indicator */}
                                <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5, mt:0.5, bgcolor: colors.bg, border:'1px solid #4a9eff44', borderRadius:1, px:1, py:0.2 }}>
                                    <Typography sx={{ fontSize:10 }}>👤</Typography>
                                    <Typography sx={{ fontSize:10, color:'#4a9eff', fontWeight:600 }}>Customer</Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={() => setEditing(e => !e)} sx={{ color: editing ? colors.primary : colors.textFaintest, '&:hover':{ color: colors.primary } }}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Edit form */}
                        {editing ? (
                            <Box sx={{ display:'flex', flexDirection:'column', gap:2, mb:2 }}>
                                <TextField label="Full Name"     size="small" value={editName}  onChange={e => setEditName(e.target.value)}  sx={inputSx} fullWidth />
                                <TextField label="Phone Number"  size="small" value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="01X-XXXXXXX" sx={inputSx} fullWidth />
                                <Box sx={{ display:'flex', gap:1 }}>
                                    <Button fullWidth onClick={handleSave} disabled={saving} sx={{ ...btnPrimary, py:1 }}>
                                        {saving ? <CircularProgress size={16} sx={{ color: colors.bgDeep }} /> : 'Save Changes'}
                                    </Button>
                                    <Button fullWidth onClick={() => setEditing(false)} sx={{ color: colors.textFaintest, textTransform:'none', borderRadius: radius.md, border:`1px solid ${colors.border}` }}>
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display:'flex', flexDirection:'column', gap:1, mb:2 }}>
                                {userData?.phone && (
                                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                                        <Typography sx={{ fontSize:12, color: colors.textFaintest, width:60 }}>Phone</Typography>
                                        <Typography sx={{ fontSize:13, color: colors.textMuted }}>{userData.phone}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                                    <Typography sx={{ fontSize:12, color: colors.textFaintest, width:60 }}>Member</Typography>
                                    <Typography sx={{ fontSize:13, color: colors.textMuted }}>{userData?.createdAt ? formatDate(userData.createdAt) : '—'}</Typography>
                                </Box>
                            </Box>
                        )}

                        {saveMsg && (
                            <Box sx={{ display:'flex', alignItems:'center', gap:0.5, mb:1 }}>
                                <CheckCircle sx={{ fontSize:14, color: colors.success }} />
                                <Typography sx={{ fontSize:12, color: colors.success }}>{saveMsg}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Stats */}
            <Box sx={{ px:3, mb:3 }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize:14, fontWeight:600, color: colors.textPrimary, mb:1.5 }}>Your Stats</Typography>
                <Box sx={{ display:'flex', gap:1.5 }}>
                    <StatChip icon={<BookOnline sx={{ fontSize:18, color: colors.primary }} />}  label="Total Bookings" value={totalBookings} color={colors.primary} />
                    <StatChip icon={<CheckCircle sx={{ fontSize:18, color: colors.info }} />}    label="Completed"     value={completed}     color={colors.info} />
                    <StatChip icon={<Person sx={{ fontSize:18, color:'#4a9eff' }} />}            label="Walk-ins"      value={walkIns}       color="#4a9eff" />
                </Box>
            </Box>

            {/* Recent bookings */}
            <Box sx={{ px:3, mb:3 }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1.5 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize:14, fontWeight:600, color: colors.textPrimary }}>Recent Bookings</Typography>
                    <Typography onClick={() => navigate('/user/bookings')} sx={{ fontSize:12, color: colors.primary, cursor:'pointer', '&:hover':{ textDecoration:'underline' } }}>See all →</Typography>
                </Box>
                <Box sx={{ bgcolor: colors.bgCard, border:`1px solid ${colors.border}`, borderRadius: radius.lg, px:2, py:1 }}>
                    {recentBookings.length === 0 ? (
                        <Box sx={{ textAlign:'center', py:4 }}>
                            <Typography sx={{ fontSize:28, mb:1 }}>📅</Typography>
                            <Typography sx={{ fontSize:13, color: colors.textFaint }}>No bookings yet</Typography>
                            <Typography onClick={() => navigate('/user/dashboard')} sx={{ fontSize:12, color: colors.primary, mt:0.5, cursor:'pointer', '&:hover':{ textDecoration:'underline' } }}>Find a barber →</Typography>
                        </Box>
                    ) : recentBookings.map(b => <BookingRow key={b.id} booking={b} />)}
                </Box>
            </Box>

            {/* Quick links */}
            <Box sx={{ px:3, mb:3 }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize:14, fontWeight:600, color: colors.textPrimary, mb:1.5 }}>Quick Links</Typography>
                <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
                    {[{ label:'📋 My Bookings', path:'/user/bookings' }, { label:'✂️ Find a Barber', path:'/user/dashboard' }].map(item => (
                        <Box key={item.path} onClick={() => navigate(item.path)} sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', bgcolor: colors.bgCard, border:`1px solid ${colors.border}`, borderRadius: radius.md, px:2, py:1.5, cursor:'pointer', transition:'all .2s', '&:hover':{ borderColor: colors.primaryBorder } }}>
                            <Typography sx={{ fontSize:13, color: colors.textPrimary }}>{item.label}</Typography>
                            <Typography sx={{ fontSize:16, color: colors.border }}>›</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Sign out */}
            <Box sx={{ px:3 }}>
                <Button fullWidth onClick={handleLogout} startIcon={<Logout sx={{ fontSize:16 }} />} sx={{ bgcolor: colors.errorBg, border:`1px solid ${colors.errorBorder}`, color: colors.error, textTransform:'none', borderRadius: radius.md, py:1.2, fontSize:13, fontWeight:600, '&:hover':{ bgcolor: colors.errorBorder } }}>
                    Sign Out
                </Button>
            </Box>
        </Box>
    );
};

export default UserProfile;